import axios from 'axios';
import { logoutUser } from '@/services/auth';
import IAccessTokenResponse from '@/models/responses/IAccessTokenResponse';
import { useUserSettingsStore } from '@/stores';
import { storeToRefs } from 'pinia';

// Get the base URL from the meta tag set by the server
const getBaseUrl = () => {
  const meta = document.head.querySelector("meta[name=baseUrl]");
  const base = meta?.textContent || "/";
  // Ensure base ends with trailing slash for consistent concatenation
  return base.endsWith("/") ? base : `${base}/`;
};

const getAbsoluteBaseUrl = () => {
  const meta = document.head.querySelector("meta[name=absoluteBaseUrl]");
  const base = meta?.textContent || "/";
  return base.endsWith("/") ? base : `${base}/`;
};

export const baseUrl = getBaseUrl();
export const absoluteBaseUrl = getAbsoluteBaseUrl();

export const basicApi = axios.create({
  baseURL: baseUrl + 'api/',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  },
});

const api = axios.create({
  baseURL: baseUrl + 'api/',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  },
});

// Rate limit retry config
const MAX_RATE_LIMIT_RETRIES = 3;
const DEFAULT_RETRY_AFTER_MS = 10_000;

function getRateLimitDelay(error: import('axios').AxiosError): number {
  const retryAfter = error.response?.headers?.['retry-after'];
  if (!retryAfter) return DEFAULT_RETRY_AFTER_MS;
  const seconds = Number(retryAfter);
  if (!isNaN(seconds)) return seconds * 1000;
  // retry-after can be an HTTP-date
  const date = Date.parse(retryAfter);
  if (!isNaN(date)) return Math.max(date - Date.now(), 0);
  return DEFAULT_RETRY_AFTER_MS;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Lock for refresh token request
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.map((callback) => callback(token));
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Request Interceptor - Attach Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access-token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const userSettingsStore = useUserSettingsStore();
  const { data: userSettings } = storeToRefs(userSettingsStore);
  if (userSettings.value.cultureName) {
    config.headers['Accept-Language'] = userSettings.value.cultureName;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response Interceptor - Handle Expired Tokens
api.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  const originalRequest = error.config;
  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    if (!isRefreshing) {
      isRefreshing = true;
      originalRequest._retry = true;
      try {
        const newToken = await tryRefreshToken();
        if (newToken) {
          onRefreshed(newToken);
          refreshSubscribers = [];
          isRefreshing = false;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (err) {
        isRefreshing = false;
        return Promise.reject(err);
      }
    } else {
      return new Promise((resolve) => {
        addRefreshSubscriber((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }
  }

  // Handle 429 Too Many Requests with retry
  if (error.response && error.response.status === 429) {
    const retryCount = originalRequest._rateLimitRetry || 0;
    if (retryCount < MAX_RATE_LIMIT_RETRIES) {
      originalRequest._rateLimitRetry = retryCount + 1;
      const waitMs = getRateLimitDelay(error);
      await delay(waitMs);
      return api(originalRequest);
    }
  }

  return Promise.reject(error);
});

basicApi.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  const originalRequest = error.config;
  if (error.response && error.response.status === 429) {
    const retryCount = originalRequest._rateLimitRetry || 0;
    if (retryCount < MAX_RATE_LIMIT_RETRIES) {
      originalRequest._rateLimitRetry = retryCount + 1;
      const waitMs = getRateLimitDelay(error);
      await delay(waitMs);
      return api(originalRequest);
    }
  }

  return Promise.reject(error);
});

export async function tryRefreshToken(): Promise<string | null> {
  try {
    // this request sets the "refresh_token" cookie implicitly
    const response = await basicApi.post<IAccessTokenResponse>('v1.0/auth/refresh-token', {});
    const data = response.data;
    localStorage.setItem('access-token', data.accessToken);
    localStorage.setItem('access-token-type', data.tokenType ?? "Bearer");
    localStorage.setItem('access-token-expiry', (Date.now() + (data.expiresIn * 1000)).toString()); // Convert to timestamp
    return response.data.accessToken;
  } catch (error) {
    logoutUser();
    return null;
  }
}

export default api;
